/**
 * Phase C 승인·실행 계약의 상수와 런타임 검증입니다. (ADR 0028 · Issue #20)
 *
 * Phase B(`agent-action.runtime.js`)가 "무엇을 하자"는 제안의 모양을 소유한다면,
 * 이 파일은 "사람이 그것을 승인했다"는 **두 단계 요청과 그 결과**의 모양을 소유합니다.
 *
 * 네 가지가 이 파일의 성격을 정합니다.
 * - **닫힘.** 요청·응답 모두 키 집합이 정확해야 합니다. 모르는 키 하나면 거절입니다.
 * - **원문 없음.** patch·command·query·executor·URL·모델 사유·Kubernetes 응답 본문이
 *   계약에 존재하지 않습니다. 없는 것은 새어 나갈 수 없습니다.
 * - **서버가 신원을 만듭니다.** executionId·dryRunDigest는 서버가 발급한 값이고
 *   브라우저는 그것을 **되읽어 확인**할 뿐 지어낼 수 없습니다.
 * - **모호함은 진실로 남깁니다.** 제출 후 타임아웃·전송 실패는 FAILED가 아니라
 *   UNKNOWN입니다. 거짓 실패는 운영자가 같은 행동을 두 번 하게 만듭니다.
 *
 * `.js`인 이유는 `agent-action.runtime.js`와 같습니다 — Node 테스트 러너와 브라우저
 * 번들이 같은 파일을 실제로 실행해야 하고, 타입 선언은 옆의 `.d.ts`가 owns합니다.
 */
import { AI_ACTION_VERBS, isSafeActionTarget } from "./agent-action.runtime.js";

export const AI_ACTION_EXECUTION_CONTRACT_VERSION = "action.v1";

export const AI_ACTION_EXECUTION_LIMITS = {
  /** 서버 발급 식별자(제안 레코드 · 실행)의 길이 상한. */
  idLength: 128,
  /** dry-run 요약 digest — sha256 hex 고정 길이. */
  digestLength: 64,
  /** Idempotency-Key 헤더의 길이 상한. */
  idempotencyKeyLength: 128,
  /** 검토 요약 항목 수 상한. 동사별 최대는 4(ADJUST_RESOURCES)입니다. */
  changeItems: 8,
  /** 요약 값 하나의 길이 상한. quantity·정수 문자열만 들어옵니다. */
  changeValueLength: 32,
  /** 승인 요청 본문 바이트 상한. 파싱 **전에** 적용합니다. */
  requestBytes: 4096,
  /** 승인 응답 바이트 상한. */
  responseBytes: 8192,
};

/** 두 단계. DRY_RUN이 검토를 열고 CONFIRM이 그 검토를 지목해 확정합니다. */
export const AI_ACTION_STAGES = ["DRY_RUN", "CONFIRM"];

/**
 * 실행 레코드의 닫힌 상태입니다.
 *
 * UNKNOWN이 있는 이유가 이 집합의 핵심입니다 — 제출 이후의 타임아웃·전송 단절은
 * "일어나지 않았다"가 아니라 "일어났는지 모른다"이고, 둘을 같은 낱말로 적으면
 * 감사가 거짓말을 합니다.
 */
export const AI_ACTION_STATUSES = [
  "PENDING",
  "DRY_RUN_READY",
  "EXECUTING",
  "EXECUTED",
  "FAILED",
  "UNKNOWN",
  "REJECTED",
];

/** CONFIRM의 결과. 상태 중 종단 셋과 같은 낱말입니다. */
export const AI_ACTION_OUTCOMES = ["EXECUTED", "FAILED", "UNKNOWN"];

/**
 * 검토 요약이 가리킬 수 있는 필드입니다. **닫힌 열거**이므로 화면이 문구를 고르고,
 * 서버가 만든 자유 문장이 라벨이 되는 경로가 없습니다.
 */
export const AI_ACTION_CHANGE_FIELDS = [
  "REPLICAS",
  "REVISION",
  "RESTART_TRIGGER",
  "CPU_REQUEST",
  "MEMORY_REQUEST",
  "CPU_LIMIT",
  "MEMORY_LIMIT",
];

/** 결정적 실패의 닫힌 사유. upstream 원문·스택은 계약에 없습니다. */
export const AI_ACTION_FAILURE_CODES = [
  "VALIDATION_REJECTED",
  "PERMISSION_DENIED",
  "TARGET_CHANGED",
  "CONFLICT",
  "NOT_SUPPORTED",
  "UPSTREAM_ERROR",
];

/** 승인 경로가 돌려줄 수 있는 안정 오류 코드입니다. 화면이 문구를 고릅니다. */
export const AI_ACTION_ERROR_CODES = [
  "action_disabled",
  "action_forbidden",
  "action_not_found",
  "action_invalid_request",
  "action_origin_rejected",
  "action_idempotency_required",
  "action_idempotency_conflict",
  "action_dry_run_required",
  "action_dry_run_expired",
  "action_dry_run_mismatch",
  "action_target_changed",
  "action_conflict",
  "action_not_supported",
  "action_busy",
  "action_store_unavailable",
  "action_upstream_unavailable",
];

/**
 * Holmes에게 보내는 사후 검증 보고의 **정확한 7개 필드**입니다.
 * (holmesgpt `holmes/core/action_result_contract.py`의 versioned form)
 */
export const AI_ACTION_RESULT_KEYS = [
  "contractVersion",
  "executionId",
  "proposalId",
  "verb",
  "target",
  "outcome",
  "executedAt",
];

/** 어떤 철자로도 실을 수 없는 키입니다. executor를 실으면 보고가 권한 주장이 됩니다. */
export const AI_ACTION_RESULT_FORBIDDEN_KEYS = ["executor", "params", "rationale", "output"];

/** 보고는 **끝난 진실**만 담습니다. UNKNOWN은 보고하지 않습니다. */
export const AI_ACTION_RESULT_OUTCOMES = ["EXECUTED", "FAILED"];

/** 경로·헤더·질의 어디에 실려도 안전한 식별자. Phase B와 같은 집합입니다. */
const SAFE_ID = /^[A-Za-z0-9._-]+$/;
const HEX64 = /^[a-f0-9]{64}$/;
const RFC3339 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
/** 요약 값 — 정수 또는 Kubernetes quantity 부분집합. 자유 문자열이 아닙니다. */
const CHANGE_VALUE = /^(?:\d+|(?:\d+|\d*\.\d+)(?:m|k|M|G|T|P|E|Ki|Mi|Gi|Ti|Pi|Ei)?)$/;

function bag(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : undefined;
}

/** 키 집합이 정확한지. 필수는 모두 있고, 그 밖은 선택 목록 안에만 있어야 합니다. */
function closed(value, required, optional = []) {
  for (const key of required) if (!(key in value)) return false;
  for (const key of Object.keys(value)) {
    if (!required.includes(key) && !optional.includes(key)) return false;
  }
  return true;
}

function identifier(value, max = AI_ACTION_EXECUTION_LIMITS.idLength) {
  return typeof value === "string" && value.length >= 1 && value.length <= max && SAFE_ID.test(value);
}

/**
 * Idempotency-Key의 **문법**만 봅니다.
 *
 * 같은 키가 같은 요청인지는 서버가 소유자·제안·단계·정규화된 본문에 묶어 판정합니다 —
 * 문자열만 보고 재생을 결정하면 다른 요청이 남의 응답을 받습니다.
 */
export function isSafeIdempotencyKey(value) {
  return identifier(value, AI_ACTION_EXECUTION_LIMITS.idempotencyKeyLength);
}

export function isSafeDryRunDigest(value) {
  return typeof value === "string" && HEX64.test(value);
}

/**
 * Phase B 카드와 Phase C 승인을 잇는 참조입니다.
 *
 * 화면이 되돌려 보내는 것은 `recordId` 하나뿐이고, 그 값은 서버가 발급했습니다 —
 * 모델이 쓴 문자열로 승인 대상을 고를 수 있는 경로가 없습니다.
 */
export function isSafeApprovalRef(value) {
  const ref = bag(value);
  return Boolean(ref) && closed(ref, ["proposalId", "recordId"]) &&
    identifier(ref.proposalId) && identifier(ref.recordId);
}

/** 승인 참조 목록. 상한을 넘거나 중복 recordId가 있으면 전부 버립니다. */
export function safeApprovalRefs(value) {
  if (!Array.isArray(value) || value.length > 8) return [];
  const seen = new Set();
  const out = [];
  for (const ref of value) {
    if (!isSafeApprovalRef(ref) || seen.has(ref.recordId)) return [];
    seen.add(ref.recordId);
    out.push(ref);
  }
  return out;
}

/**
 * 승인 요청 본문. `stage`가 판별자이고 그 밖의 모양은 존재하지 않습니다.
 *
 * DRY_RUN은 인자가 없습니다 — 무엇을 할지는 서버가 보관한 제안 스냅숏이 정하고,
 * 브라우저가 되돌려 보낸 내용은 어떤 경우에도 실행 근거가 되지 않습니다.
 */
export function isSafeApprovalRequest(value) {
  const body = bag(value);
  if (!body || !AI_ACTION_STAGES.includes(body.stage)) return false;
  if (body.stage === "DRY_RUN") return closed(body, ["stage"]);
  return (
    closed(body, ["stage", "executionId", "dryRunDigest"]) &&
    identifier(body.executionId) &&
    isSafeDryRunDigest(body.dryRunDigest)
  );
}

function changeItem(value) {
  const item = bag(value);
  if (!item || !closed(item, ["field", "to"], ["from"])) return false;
  if (!AI_ACTION_CHANGE_FIELDS.includes(item.field)) return false;
  for (const key of ["from", "to"]) {
    const raw = item[key];
    if (raw === undefined) continue;
    if (typeof raw !== "string" || raw.length > AI_ACTION_EXECUTION_LIMITS.changeValueLength) return false;
    if (!CHANGE_VALUE.test(raw)) return false;
  }
  return true;
}

/** 검토 요약. 서버가 만든 값만 담고, 만료가 붙어 있어 오래된 검토로 확정할 수 없습니다. */
export function isSafeActionReview(value) {
  const review = bag(value);
  if (!review || !closed(review, ["dryRunDigest", "expiresAt", "changes"])) return false;
  if (!isSafeDryRunDigest(review.dryRunDigest) || !RFC3339.test(review.expiresAt)) return false;
  if (!Array.isArray(review.changes) || review.changes.length > AI_ACTION_EXECUTION_LIMITS.changeItems) return false;
  const seen = new Set();
  for (const item of review.changes) {
    if (!changeItem(item) || seen.has(item.field)) return false;
    seen.add(item.field);
  }
  return true;
}

/**
 * 승인 응답 하나의 모양입니다.
 *
 * 화면은 서버가 이미 검증한 값을 한 번 더 봅니다 — 중간 프록시가 바꿔치기한 프레임이나
 * 미래의 서버 버전이 승인 UI의 상태 기계에 그대로 들어오면 안 됩니다.
 */
export function isSafeApprovalResponse(value) {
  const out = bag(value);
  const required = ["contractVersion", "executionId", "proposalId", "stage", "status", "verb", "target", "updatedAt"];
  if (!out || !closed(out, required, ["review", "outcome", "failureCode"])) return false;
  if (out.contractVersion !== AI_ACTION_EXECUTION_CONTRACT_VERSION) return false;
  if (!identifier(out.executionId) || !identifier(out.proposalId)) return false;
  if (!AI_ACTION_STAGES.includes(out.stage) || !AI_ACTION_STATUSES.includes(out.status)) return false;
  if (!AI_ACTION_VERBS.includes(out.verb) || !isSafeActionTarget(out.target)) return false;
  if (!RFC3339.test(out.updatedAt)) return false;
  if (out.review !== undefined && !isSafeActionReview(out.review)) return false;
  if (out.outcome !== undefined && !AI_ACTION_OUTCOMES.includes(out.outcome)) return false;
  if (out.failureCode !== undefined && !AI_ACTION_FAILURE_CODES.includes(out.failureCode)) return false;
  /* 상태와 부가 필드가 서로를 부정하지 않아야 합니다. FAILED가 아닌데 사유가 붙거나,
     DRY_RUN_READY인데 검토가 없으면 그 응답은 자기 자신과 어긋난 것입니다. */
  if ((out.failureCode !== undefined) !== (out.status === "FAILED")) return false;
  if (out.status === "DRY_RUN_READY" && out.review === undefined) return false;
  return true;
}

/**
 * Holmes에게 보낼 사후 검증 보고의 모양입니다. **정확히 7개 필드**이고
 * outcome은 EXECUTED·FAILED 둘뿐입니다 — 모르는 것을 보고하지 않습니다.
 */
export function isSafeActionResult(value) {
  const result = bag(value);
  if (!result || !closed(result, AI_ACTION_RESULT_KEYS)) return false;
  if (result.contractVersion !== AI_ACTION_EXECUTION_CONTRACT_VERSION) return false;
  if (!identifier(result.executionId) || !identifier(result.proposalId)) return false;
  if (!AI_ACTION_VERBS.includes(result.verb) || !isSafeActionTarget(result.target)) return false;
  if (!AI_ACTION_RESULT_OUTCOMES.includes(result.outcome)) return false;
  return RFC3339.test(result.executedAt);
}
